import React from "react";
import { getRequest } from "sharedUtils/httpUtils";
import MUIDataTable from "mui-datatables";
import { convertToNumericMonthAndDayAndYearAndTime } from "sharedUtils/timeUtils";
import { ThemeProvider, createTheme } from "@material-ui/core/styles";
import { CheckIconBlack } from "../../icons";
import PenIcon from "components/icons/PenIcon";

const { DateTime } = require("luxon");

class FlexoffersMerchantData extends React.Component {
    constructor(props) {
        super(props);
        this.tableRef = React.createRef();

        this.state = {
            merchants: [],
            merchantsLoading: false,
            MerchantsTable: {
                title: "Affiliate merchants",
                options: {
                    selectableRows: "multiple",
                    rowsPerPage: 100,
                    sortOrder: {
                        name: 'merchantName',
                        direction: 'asc'
                    },
                },
                getTheme: () => createTheme({
                    typography: {
                        fontFamily: "'Urbanist', 'sf_pro_displayregular', sans-serif",
                    },
                    overrides: {
                        MUIDataTableBodyRow: {
                            root: {
                                '&:nth-child(odd)': {
                                    backgroundColor: '#F9F9F9'
                                },
                                cursor: 'pointer'
                            }
                        },
                    }
                })
            },
        };
    }

    componentDidMount = () => {
        window.scrollTo({ left: 0, top: 0, behavior: 'auto' });
        this.getMerchants();
    };

    getMerchants = async () => {
        let url = `/api/Merchant/GetFlexoffersMerchants`;

        this.setState({ merchantsLoading: true });

        try {
            const merchants = await getRequest(url);

            this.setState({
                merchants,
                merchantsLoading: false,
            });
        } catch (error) {
            console.error("Error fetching merchants:", error);
            this.setState({ merchants: [], merchantsLoading: false });
        }
    };

    onTabChange = (tab) => {
        this.setState({ tab });
    }

    render() {
        const columns = [
            {
                name: "hostname",
                label: "Hostname",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: value => <span>{value}</span>
                }
            },
            {
                name: "name",
                label: "Name",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: value => <b>{value}</b>
                }
            },
            {
                name: "flexoffersMerchantId",
                label: "Merchant ID",
                options: {
                    filter: true,
                    sort: true
                }
            },
            {
                name: "domainUrl",
                label: "Domain URL",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: value =>
                        value ? <a href={value} target="_blank" rel="noopener noreferrer">{value}</a> : "-"
                }
            },
            {
                name: "country",
                label: "Country",
                options: {
                    filter: true,
                    sort: true
                }
            },
            {
                name: "categoryNames",
                label: "Categories",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: value =>
                        value ? value.split(",").map(cat => <div key={cat}>{cat.trim()}</div>) : "-"
                }
            },
            {
                name: "programStatus",
                label: "Program Status",
                options: {
                    filter: true,
                    sort: true
                }
            },
            {
                name: "applicationStatus",
                label: "Application Status",
                options: {
                    filter: true,
                    sort: true
                }
            },
            {
                name: "payout",
                label: "Payout",
                options: {
                    filter: true,
                    sort: true
                }
            },
            {
                name: "sevenDayEpc",
                label: "7-Day EPC",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: value =>
                        value ? `$${parseFloat(value).toFixed(2)}` : "-"
                }
            },
            {
                name: "thirtyDayEpc",
                label: "30-Day EPC",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: value =>
                        value ? `$${parseFloat(value).toFixed(2)}` : "-"
                }
            },
            {
                name: "threeMonthEpc",
                label: "3-Month EPC",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: value =>
                        value ? `$${parseFloat(value).toFixed(2)}` : "-"
                }
            },
            {
                name: "allowsDeeplinking",
                label: "Allows Deeplinking",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: value =>
                        value ? "✅ Yes" : "❌ No"
                }
            },
            {
                name: "flexLinks",
                label: "Flex Links",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: value =>
                        value ? "✅ Yes" : "❌ No"
                }
            },
            {
                name: "productAdvertiser",
                label: "Product Advertiser",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: value =>
                        value ? "✅ Yes" : "❌ No"
                }
            },
            {
                name: "promotionalAdvertiser",
                label: "Promotional Advertiser",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: value =>
                        value ? "✅ Yes" : "❌ No"
                }
            },
            {
                name: "deeplinkUrl",
                label: "Deeplink URL",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: value =>
                        value ? <a href={value} target="_blank" rel="noopener noreferrer">{value}</a> : "-"
                }
            },
            {
                name: "networkRank",
                label: "Network Rank",
                options: {
                    filter: true,
                    sort: true
                }
            },
            {
                name: "actions",
                label: "Actions",
                options: {
                    filter: false,
                    sort: false,
                    customBodyRender: value => {
                        if (!value) return "-";

                        let parsed;
                        try {
                            parsed = typeof value === "string" ? JSON.parse(value) : value;
                        } catch {
                            return "-";
                        }

                        if (!Array.isArray(parsed) || parsed.length === 0) {
                            return "-";
                        }

                        return (
                            <ul style={{ margin: 0, paddingLeft: "20px" }}>
                                {parsed.map((a, i) => (
                                    <li key={i}>
                                        {a.action ?? "N/A"} ({a.actionType ?? "?"}) – {a.commission ?? "N/A"}
                                    </li>
                                ))}
                            </ul>
                        );
                    }
                }
            }
        ];


        return (
            <div>
                <ThemeProvider theme={this.state.MerchantsTable.getTheme()}>
                    <MUIDataTable
                        title={
                            <div style={{ fontFamily: "'Urbanist', sans-serif" }}>
                                <p style={{ fontSize: '22px', marginTop: "20px" }}>{this.props.affiliateProgramManager?.name + " merchants" ?? "No affiliate selected"}</p>
                            </div>
                        }
                        data={this.state.merchants ?? []}
                        columns={columns}
                        options={{
                            ...this.state.MerchantsTable.options,
                            textLabels: {
                                body: { noMatch: this.state.merchantsLoading ? "LOADING..." : "No data available" },
                            },
                            selectableRows: "none",
                        }}
                        ref={this.tableRef}
                    />
                </ThemeProvider>
            </div>
        );
    }
}

export default FlexoffersMerchantData;
